import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Text, useResponsive } from '@common-fe/common-fe';

import { ModalWrapper } from '@/components';

import ReturnedProviderCheckCard from './components/ReturnedProviderCheckCard';
import useReturnedChecks from './hooks/useReturnedChecks';
import useReturnedItemsQuery from './queries/useReturnedItems.query';
import { useBadAddressReviewStore } from './stores/useBadAddressReview.store';

interface Props {
  storeKey?: string;
}

const ReturnedChecksModal: React.FC<Props> = ({ storeKey }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { isModalVisible, handleCloseModal } = useBadAddressReviewStore(storeKey);
  const { providerChecks } = useReturnedItemsQuery();

  const { checksProps, areCheckDecisionsComplete } = useReturnedChecks({
    providerChecks,
    participantChecks: [],
    excludeCheckPaymentMethod: false,
  });

  const { checkDecisions, onSelectCheckAction } = checksProps;

  const isNextDisabled = useMemo(
    () => !areCheckDecisionsComplete,
    [areCheckDecisionsComplete]
  );

  if (!isModalVisible) {
    return null;
  }

  return (
    <ModalWrapper visible onSetVisible={handleCloseModal} isClosingByClickOutsideBlocked>
      <Box align="center" margin={{ bottom: isMobile ? 'spacing16' : 'spacing24' }}>
        <Text
          size={isMobile ? 'xxlarge' : '2xl'}
          color="textBody"
          weight="bold"
          textAlign="center"
          data-testid="returned-checks-modal_title"
        >
          {t('Returned Items')}
        </Text>
      </Box>

      <Box
        pad={isMobile ? 'spacing8' : 'spacing24'}
        round={isMobile ? 'container1Round' : 'moduleRound'}
        background="module"
        gap={isMobile ? 'spacing8' : 'spacing16'}
        data-testid="returned-checks-modal_content"
      >
        {providerChecks.map((check) => (
          <ReturnedProviderCheckCard
            key={check.lockbox_id}
            check={check}
            decision={checkDecisions[check.lockbox_id as number]}
            onSelect={onSelectCheckAction}
          />
        ))}
      </Box>

      <Box
        direction={isMobile ? 'column-reverse' : 'row'}
        justify="end"
        gap="spacing12"
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        pad={isMobile ? { horizontal: 'spacing4' } : undefined}
      >
        <AppButton
          testId="returned-checks-modal_back-btn"
          onClick={handleCloseModal}
          buttonType="secondary"
          width={isMobile ? '100%' : '180px'}
        >
          {t('Back')}
        </AppButton>
        <AppButton
          testId="returned-checks-modal_next-btn"
          onClick={handleCloseModal}
          disabled={isNextDisabled}
          width={isMobile ? '100%' : '180px'}
        >
          {t('Next')}
        </AppButton>
      </Box>
    </ModalWrapper>
  );
};

export default ReturnedChecksModal;
